import type { ControlIntent } from '../core/InputController';
import type { PlayerNetworkState } from '../entities/PlayerEntity';
import type { WeaponNetworkState } from './ArenaWeapons';
import type { RocketNetworkState } from './RocketSystem';

export interface OnlineSnapshot {
  phase: 'countdown' | 'play' | 'goal' | 'paused' | 'complete';
  time: number;
  phaseTimer: number;
  score: number[];
  golden: boolean;
  focus: string[];
  players: PlayerNetworkState[];
  weapons: WeaponNetworkState[];
  rockets?: RocketNetworkState[];
  ball: { position: number[]; velocity: number[]; ownerId: string | null; state: 'free' | 'possessed' | 'shot' | 'held'; explosive: boolean };
  boxes: boolean[];
  bombs: Array<{ position: number[]; rotation: number[]; fuse: number }>;
  banner: { main: string; sub: string; tone: string; visible: boolean };
  status: string;
  audio: Array<{ name: string; args: unknown[] }>;
}

export class OnlineSession {
  role: 'host' | 'guest' | 'spectator' | null = null;
  started = false;
  private verified = false;
  private roomCode: string | null = null;
  private socket: WebSocket | null = null;
  private intentionalClose = false;
  private lastSend = 0;
  private pendingInput: ControlIntent | null = null;
  private lastError = '';
  private captchaWidget: number | null = null;
  private captchaLoading: Promise<void> | null = null;
  private readonly dialog = document.querySelector<HTMLDialogElement>('#online-dialog')!;
  private readonly form = document.querySelector<HTMLFormElement>('#online-form')!;
  private readonly status = document.querySelector<HTMLElement>('#online-status')!;

  constructor(private readonly callbacks: {
    start: (role: 'host' | 'guest' | 'spectator') => void;
    snapshot: (state: OnlineSnapshot) => void;
    input: (input: ControlIntent) => void;
    pause: () => void;
    close: (reason: string) => void;
    promote: (state: OnlineSnapshot | null) => void;
    resume: (role: 'guest', state: OnlineSnapshot | null) => void;
    networkLost: (role: 'host' | 'guest' | 'spectator', reason: string) => void;
  }) {
    document.querySelector('#menu-online')!.addEventListener('click', () => this.open());
    document.querySelector('#online-leave')!.addEventListener('click', () => this.leave());
    document.querySelector('#online-exit-match')!.addEventListener('click', () => this.send({ type: 'leave-room' }));
    document.querySelector('#online-leave-room')!.addEventListener('click', () => this.send({ type: 'leave-room' }));
    document.querySelector('#online-open-lobby')!.addEventListener('click', () => this.dialog.showModal());
    document.querySelector('#online-back-match')!.addEventListener('click', () => this.dialog.close());
    document.querySelector('#online-start')!.addEventListener('click', () => this.send({ type: 'start' }));
    document.querySelector('#online-create-form')!.addEventListener('submit', event => {
      event.preventDefault();
      this.send({ type: 'create', name: document.querySelector<HTMLInputElement>('#online-room-name')!.value });
    });
    document.querySelector('#online-chat-form')!.addEventListener('submit', event => {
      event.preventDefault();
      const input = document.querySelector<HTMLInputElement>('#online-chat-text')!;
      if (input.value.trim() && this.send({ type: 'chat', text: input.value })) input.value = '';
    });
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); if (this.started) this.dialog.close(); else this.leave(); });
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      const token = this.captchaWidget !== null ? window.grecaptcha?.getResponse(this.captchaWidget) : '';
      if (!token) { this.status.textContent = 'Completa el reCAPTCHA antes de entrar.'; return; }
      this.connect(token);
    });
  }

  open(): void {
    this.status.textContent = 'Verifica que eres humano para entrar al lobby.';
    this.refreshPanels();
    this.dialog.showModal();
    document.querySelector<HTMLInputElement>('#online-nickname')!.focus();
    void this.loadCaptcha().catch(() => { this.status.textContent = 'No se pudo cargar reCAPTCHA. Revisa la conexión y vuelve a intentar.'; this.captchaLoading = null; });
  }

  private async loadCaptcha(): Promise<void> {
    if (this.captchaWidget !== null) return;
    if (this.captchaLoading) return this.captchaLoading;
    this.captchaLoading = (async () => {
      const response = await fetch(`${import.meta.env.BASE_URL}api/online/config`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Configuración no disponible.');
      const config = await response.json() as { siteKey: string; configured: boolean };
      if (!config.configured || !config.siteKey) throw new Error('reCAPTCHA no configurado.');
      if (!window.grecaptcha?.render) await new Promise<void>((resolve, reject) => {
        window.onFutbolCaptchaReady = resolve;
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onFutbolCaptchaReady&render=explicit&hl=es';
        script.async = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      this.captchaWidget = window.grecaptcha!.render('online-captcha', { sitekey: config.siteKey, theme: 'dark', size: window.innerWidth < 380 ? 'compact' : 'normal',
        'expired-callback': () => { this.status.textContent = 'reCAPTCHA vencido. Vuelve a completarlo.'; },
        'error-callback': () => { this.status.textContent = 'reCAPTCHA no pudo conectarse. Inténtalo de nuevo.'; } });
    })();
    return this.captchaLoading;
  }

  private connect(captchaToken: string): void {
    if (this.socket) return;
    const url = new URL(`${import.meta.env.BASE_URL}api/online/ws`, window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    this.intentionalClose = false;
    this.lastError = '';
    this.status.textContent = 'Conectando con Oracle…';
    this.form.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach(button => { button.disabled = true; });
    const socket = this.socket = new WebSocket(url);
    const timeout = window.setTimeout(() => {
      this.status.textContent = 'Oracle no responde. Comprueba la conexión e intenta otra vez.';
      socket.close();
    }, 12000);
    socket.onopen = () => socket.send(JSON.stringify({ type: 'enter', captchaToken,
      nickname: document.querySelector<HTMLInputElement>('#online-nickname')!.value }));
    socket.onmessage = event => {
      const message = JSON.parse(event.data);
      if (message.type === 'welcome') {
        clearTimeout(timeout);
        this.verified = true;
        document.querySelector('#online-self')!.textContent = message.nickname;
        document.querySelector('#online-chat-list')!.replaceChildren();
        for (const entry of message.history) this.appendChat(entry);
        this.status.textContent = 'Crea una partida o entra a una sala como jugador o espectador.';
        this.refreshPanels();
      } else if (message.type === 'lobbies') this.renderLobbies(message.rooms, message.onlineCount);
      else if (message.type === 'chat') this.appendChat(message.entry);
      else if (message.type === 'room') {
        this.roomCode = message.code;
        this.role = message.role;
        document.querySelector('#online-room-summary')!.textContent = `${message.name} · ${message.code}`;
        document.querySelector('#online-room-members')!.textContent = `AZUL: ${message.host} · ROJO: ${message.guest ?? 'Esperando rival'} · ${message.spectators} espectadores`;
        const start = document.querySelector<HTMLButtonElement>('#online-start')!;
        start.classList.toggle('hidden', this.role !== 'host' || this.started);
        start.disabled = !message.guest || message.started;
        this.status.textContent = this.role === 'host' ? message.started && !message.guest ? 'Eres AZUL. El rival se desconectó; la partida continúa contra la CPU y puede entrar otro jugador.' : 'Eres AZUL. Pulsa JUGAR cuando haya un rival.' : this.role === 'guest' ? message.started ? 'Te reincorporaste a una partida en curso.' : 'Eres ROJO. Esperando a que el creador inicie.' : 'Eres espectador. Verás la partida cuando comience.';
        this.refreshPanels();
      } else if (message.type === 'start' && this.role) {
        this.started = true;
        document.querySelector<HTMLButtonElement>('#online-start')!.disabled = true;
        this.dialog.close();
        document.querySelector('#online-match-actions')!.classList.remove('hidden');
        document.querySelector('#online-back-match')!.classList.remove('hidden');
        this.callbacks.start(this.role);
      } else if (message.type === 'resume' && this.role === 'guest') {
        this.started = true;
        this.dialog.close();
        document.querySelector('#online-match-actions')!.classList.remove('hidden');
        document.querySelector('#online-back-match')!.classList.remove('hidden');
        this.callbacks.resume('guest', message.state);
      } else if (message.type === 'promote') {
        this.role = 'host';
        this.started = true;
        this.callbacks.promote(message.state);
      } else if (message.type === 'player-left') {
        this.status.textContent = message.message;
      } else if (message.type === 'state' && this.role !== 'host' && this.started) this.callbacks.snapshot(message.state);
      else if (message.type === 'input' && this.role === 'host') this.callbacks.input(message.input);
      else if (message.type === 'pause') this.callbacks.pause();
      else if (message.type === 'room-ended') {
        const wasPlaying = this.started;
        this.started = false;
        this.role = null;
        this.roomCode = null;
        document.querySelector('#online-match-actions')!.classList.add('hidden');
        document.querySelector('#online-back-match')!.classList.add('hidden');
        if (wasPlaying) this.callbacks.close(message.error);
        this.status.textContent = message.error;
        this.refreshPanels();
        if (!this.dialog.open) this.dialog.showModal();
      } else if (message.type === 'error') this.status.textContent = this.lastError = message.error;
    };
    socket.onerror = () => { this.status.textContent = 'No se pudo conectar con Oracle. Revisa tu conexión.'; };
    socket.onclose = () => {
      clearTimeout(timeout);
      const wasPlaying = this.started;
      const disconnectedRole = this.role;
      this.socket = null;
      this.verified = false;
      this.roomCode = null;
      this.role = null;
      this.started = false;
      this.pendingInput = null;
      document.querySelector('#online-match-actions')!.classList.add('hidden');
      document.querySelector('#online-back-match')!.classList.add('hidden');
      if (this.captchaWidget !== null) window.grecaptcha?.reset(this.captchaWidget);
      this.refreshPanels();
      this.form.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach(button => { button.disabled = false; });
      if (!this.intentionalClose) {
        const reason = this.lastError || 'Conexión cerrada. Crea otra sala para volver a jugar.';
        this.status.textContent = reason;
        if (wasPlaying && disconnectedRole) this.callbacks.networkLost(disconnectedRole, reason);
      }
    };
  }

  private refreshPanels(): void {
    this.form.classList.toggle('hidden', this.verified);
    document.querySelector('#online-hub')!.classList.toggle('hidden', !this.verified);
    document.querySelector('#online-room-info')!.classList.toggle('hidden', !this.roomCode);
    document.querySelector<HTMLButtonElement>('#online-create')!.disabled = !!this.roomCode;
  }

  private renderLobbies(rooms: Array<{ code: string; name: string; host: string; guest: string | null; started: boolean; spectators: number }>, count: number): void {
    document.querySelector('#online-count')!.textContent = `${count} conectados`;
    const list = document.querySelector('#online-rooms')!;
    list.replaceChildren();
    if (!rooms.length) {
      const empty = document.createElement('p');
      empty.textContent = 'No hay partidas creadas. ¡Crea la primera!';
      list.append(empty);
    }
    for (const room of rooms) {
      const card = document.createElement('article');
      card.className = 'online-room-card';
      card.dataset.code = room.code;
      const title = document.createElement('h4');
      title.textContent = room.name;
      const details = document.createElement('p');
      details.textContent = `${room.started ? 'EN JUEGO' : 'EN ESPERA'} · ${room.guest ? '2' : '1'}/2 jugadores · ${room.spectators} espectadores`;
      const names = document.createElement('p');
      names.textContent = `AZUL: ${room.host} · ROJO: ${room.guest ?? 'Libre'}`;
      const actions = document.createElement('div');
      actions.className = 'options-actions';
      for (const spectator of [false, true]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.action = spectator ? 'spectate' : 'play';
        button.textContent = spectator ? 'VER · ESPECTADOR' : 'ENTRAR A JUGAR';
        button.disabled = !!this.roomCode || (spectator ? room.spectators >= 12 : !!room.guest);
        button.addEventListener('click', () => this.send({ type: 'join', code: room.code, spectator }));
        actions.append(button);
      }
      card.append(title, details, names, actions);
      list.append(card);
    }
  }

  private appendChat(entry: { nickname: string; text: string }): void {
    const list = document.querySelector('#online-chat-list')!;
    const message = document.createElement('p');
    const author = document.createElement('strong');
    author.textContent = `${entry.nickname}: `;
    message.append(author, document.createTextNode(entry.text));
    list.append(message);
    while (list.childElementCount > 60) list.firstElementChild!.remove();
    list.scrollTop = list.scrollHeight;
  }

  sendInput(input: ControlIntent): void {
    if (!this.started || this.role !== 'guest') return;
    const pending = this.pendingInput;
    this.pendingInput = { ...input, passPressed: input.passPressed || !!pending?.passPressed,
      punchPressed: input.punchPressed || !!pending?.punchPressed, shootPressed: input.shootPressed || !!pending?.shootPressed,
      shootReleased: input.shootReleased || !!pending?.shootReleased, sprint: input.sprint || !!pending?.sprint };
    if (performance.now() - this.lastSend < 1000 / 30) return;
    this.send({ type: 'input', input: this.pendingInput });
    this.pendingInput = null;
    this.lastSend = performance.now();
  }

  send(message: unknown): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN || this.socket.bufferedAmount > 128 * 1024) return false;
    this.socket.send(JSON.stringify(message));
    return true;
  }

  leave(): void {
    const wasPlaying = this.started;
    this.intentionalClose = true;
    this.started = false;
    this.role = null;
    this.verified = false;
    this.roomCode = null;
    this.socket?.close();
    this.dialog.close();
    document.querySelector('#online-match-actions')!.classList.add('hidden');
    document.querySelector('#online-back-match')!.classList.add('hidden');
    if (wasPlaying) this.callbacks.close('Saliste de la partida online.');
  }
}

declare global {
  interface Window {
    onFutbolCaptchaReady?: () => void;
    grecaptcha?: {
      render(container: string, options: Record<string, unknown>): number;
      getResponse(widget: number): string;
      reset(widget: number): void;
    };
  }
}
